import { useEffect, useMemo, useState } from 'react';
import { endOfDay, format, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { hu } from 'date-fns/locale';
import { PiClockCounterClockwiseDuotone } from 'react-icons/pi';

import { Tooltip } from '@material-ui/core';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader, PopupDateRangeQuickSelector, PopupToolbar, PopupToolbarGroup, PopupToolbarScrollGroup, PopupToolbarChip, PopupExportButtons } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';
import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSoftStatusClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';
import { parseTsToDate } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';
import { buildExportFilename, exportPopupTable } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const INITIAL_DATE_RANGE = { from: '', to: '', quickRange: 'ALL' };
const INITIAL_SORT = { key: 'ts', direction: 'desc' };
const STATUS_BADGE_CLASS = 'px-8 py-5 rounded-lg font-semibold whitespace-nowrap';
const STATUS_CELL_CLASS = 'flex flex-col text-12 px-8 py-5 rounded-lg';
const STATUS_INLINE_CELL_CLASS = 'inline-flex items-center gap-6 px-8 py-5 rounded-lg text-12 whitespace-nowrap';

function safeDate(value) { return parseTsToDate(value); }

function formatInputDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) { return ''; }
    return format(date, 'yyyy-MM-dd');
}

function formatDisplayDate(value) {
    const date = safeDate(value);
    return date ? format(date, 'yyyy.MM.dd.', { locale: hu }) : '';
}

function getDateRangeSubtitle(dateRange) {
    const from = formatDisplayDate(dateRange?.from);
    const to = formatDisplayDate(dateRange?.to);

    if (from && to) { return 'Showing data from ' + from + ' to ' + to; }
    if (from) { return 'Showing data from ' + from; }
    if (to) { return 'Showing data until ' + to; }

    return null;
}

function getStatusClass(color, baseClassName) {
    return baseClassName + ' ' + getSoftStatusClass(color);
}

function toText(value) {
    if (value == null) { return '-'; }
    if (value instanceof Date) { return value.toLocaleString('hu-HU'); }
    if (typeof value === 'object') { return JSON.stringify(value); }
    return String(value);
}

function formatApe(value) {
    const numberValue = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
    return Number.isFinite(numberValue) ? numberValue.toFixed(4) : toText(value);
}

function formatDate(value) {
    const date = safeDate(value);
    return date ? format(date, 'yyyy.MM.dd', { locale: hu }) : '-';
}

function getAllFeatureKeys(list) {
    const set = new Set();

    for (const entry of (Array.isArray(list) ? list : [])) {
        const features = entry?.features && typeof entry.features === 'object' ? entry.features : null;
        if (features) { Object.keys(features).forEach((key) => set.add(key)); }
    }

    return Array.from(set);
}

function overallAccurate(row) {
    if (typeof row?.accurate === 'boolean') { return row.accurate; }
    if (typeof row?.meta?.accurate === 'boolean') { return row.meta.accurate; }

    const features = row?.features && typeof row.features === 'object' ? row.features : null;

    if (features) {
        let seen = false;

        for (const key of Object.keys(features)) {
            const accurate = features[key]?.accurate;

            if (typeof accurate === 'boolean') {
                seen = true;
                if (accurate === false) { return false; }
            }
        }

        if (seen) { return true; }
    }

    return undefined;
}

function formatAccurateValue(value) {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Missing';
}

function getFeatureParts(row, key) {
    const value = row?.features?.[key];

    const ape = typeof value === 'object' && value ? value.ape : value;
    const tolerance = typeof value === 'object' && value ? value.tolerance : undefined;
    const accurate = typeof value === 'object' && value ? value.accurate : undefined;

    return { ape, tolerance, accurate };
}

function formatFeatureExportValue(row, key) {
    const { ape, tolerance, accurate } = getFeatureParts(row, key);
    const accurateText = accurate === true ? 'Yes' : accurate === false ? 'No' : '-';

    return 'APE: ' + formatApe(ape) + '; Tol: ' + toText(tolerance) + '; Accurate: ' + accurateText;
}

function renderAccurateBadge(value) {
    if (value === true) { return (<div className={getStatusClass('green', STATUS_BADGE_CLASS)}>Yes</div>); }
    if (value === false) { return (<div className={getStatusClass('red', STATUS_BADGE_CLASS)}>No</div>); }
    return (<div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>Missing</div>);
}

function renderFeatureCell(row, key) {
    const { ape, tolerance, accurate } = getFeatureParts(row, key);

    const accurateText = accurate === true ? 'Yes' : accurate === false ? 'No' : '-';
    const color = accurate === true ? 'green' : accurate === false ? 'red' : 'gray';

    return (
        <div className={getStatusClass(color, STATUS_INLINE_CELL_CLASS) + ' w-full'}>
            <span><span className='font-semibold opacity-60'>APE:</span> {formatApe(ape)}</span>
            <span className='opacity-40'>•</span>
            <span><span className='font-semibold opacity-60'>Tol:</span> {toText(tolerance)}</span>
            <span className='opacity-40'>•</span>
            <span className='font-semibold'>{accurateText}</span>
        </div>
    );
}

export default function ModelValidationHistoryPopup(props) {
    const { popup, closePopup, getOnDemandResource, ensureOnDemandResource, ts } = useOptimizationPipeline();

    const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
    const [visibleFeatureKeys, setVisibleFeatureKeys] = useState([]);
    const [accurateFilter, setAccurateFilter] = useState('has');
    const [tableSort, setTableSort] = useState(INITIAL_SORT);

    const modelValidationResource = getOnDemandResource('modelValidationLogDataList');
    const modelValidationLogDataList = modelValidationResource?.data;
    const modelValidationLogDataListLoading = modelValidationResource?.loading;
    const modelValidationLogDataListError = modelValidationResource?.error;

    useEffect(() => {
        if (!popup || popup.type !== 'MODEL_VALIDATION_HISTORY') return;
        ensureOnDemandResource('modelValidationLogDataList');
    }, [popup, ensureOnDemandResource]);

    useEffect(() => {
        if (!popup || popup.type !== 'MODEL_VALIDATION_HISTORY') return;
        setTableSort(INITIAL_SORT);
        setAccurateFilter('has');
    }, [popup?.type, popup?.requestToken]);

    const rows = useMemo(() => {
        return Array.isArray(modelValidationLogDataList) ? modelValidationLogDataList : [];
    }, [modelValidationLogDataList]);

    const featureKeys = useMemo(() => {
        return getAllFeatureKeys(rows);
    }, [rows]);

    useEffect(() => {
        setVisibleFeatureKeys(featureKeys);
    }, [featureKeys]);

    const dateBounds = useMemo(() => {
        const dates = rows
            .map((row) => safeDate(row?.ts))
            .filter(Boolean)
            .sort((a, b) => a.getTime() - b.getTime());

        if (!dates.length) { return { minDate: null, maxDate: null }; }

        return {
            minDate: dates[0],
            maxDate: dates[dates.length - 1]
        };
    }, [rows]);

    useEffect(() => {
        if (!dateBounds.minDate || !dateBounds.maxDate) return;

        const nextFrom = formatInputDate(dateBounds.minDate);
        const nextTo = formatInputDate(dateBounds.maxDate);

        setDateRange((prev) => {
            if (prev.from === nextFrom && prev.to === nextTo && prev.quickRange === 'ALL') { return prev; }
            return { from: nextFrom, to: nextTo, quickRange: 'ALL' };
        });
    }, [dateBounds]);

    const disabledQuickOptions = useMemo(() => {
        const { minDate, maxDate } = dateBounds;

        if (!minDate || !maxDate) {
            return {
                ALL: true,
                '1Y': true,
                '1M': true,
                '1W': true,
                '1D': true
            };
        }

        const minTime = minDate.getTime();

        return {
            ALL: false,
            '1Y': subYears(maxDate, 1).getTime() < minTime,
            '1M': subMonths(maxDate, 1).getTime() < minTime,
            '1W': subWeeks(maxDate, 1).getTime() < minTime,
            '1D': subDays(maxDate, 1).getTime() < minTime
        };
    }, [dateBounds]);

    const filteredRows = useMemo(() => {
        let nextRows = rows;

        const fromDate = safeDate(dateRange?.from);
        const toDate = safeDate(dateRange?.to);

        if (fromDate || toDate) {
            const fromTime = fromDate ? fromDate.getTime() : -Infinity;
            const toTime = toDate ? endOfDay(toDate).getTime() : Infinity;

            nextRows = nextRows.filter((row) => {
                const date = safeDate(row?.ts);
                if (!date) return false;

                const time = date.getTime();
                return time >= fromTime && time <= toTime;
            });
        }

        if (accurateFilter === 'has') {
            nextRows = nextRows.filter((row) => overallAccurate(row) === true || overallAccurate(row) === false);
        } else if (accurateFilter === 'yes') {
            nextRows = nextRows.filter((row) => overallAccurate(row) === true);
        } else if (accurateFilter === 'no') {
            nextRows = nextRows.filter((row) => overallAccurate(row) === false);
        }

        return nextRows;
    }, [rows, dateRange, accurateFilter]);

    const tableConfig = useMemo(() => {
        const baseColumns = [
            {
                key: 'ts',
                label: 'Timestamp',
                exportLabel: 'Timestamp',
                sortable: true,
                defaultSortDirection: 'desc',
                width: 140,
                minWidth: 140,
                align: 'left',
                getSortValue: (row) => row?.ts,
                exportValue: (row) => formatDate(row?.ts),
                renderCell: ({ row }) => formatDate(row?.ts)
            },
            {
                key: '_accurate',
                label: 'Accurate',
                exportLabel: 'Accurate',
                sortable: true,
                width: 120,
                minWidth: 120,
                align: 'center',
                getSortValue: (row) => {
                    const accurate = overallAccurate(row);
                    if (accurate === true) { return 1; }
                    if (accurate === false) { return 0; }
                    return -1;
                },
                exportValue: (row) => formatAccurateValue(overallAccurate(row)),
                renderCell: ({ row }) => renderAccurateBadge(overallAccurate(row))
            }
        ];

        const dynamicColumns = featureKeys
            .filter((key) => visibleFeatureKeys.includes(key))
            .map((key) => {
                return {
                    key: key,
                    exportLabel: key,
                    label: (
                        <div className='flex flex-col leading-tight overflow-hidden' style={{ maxWidth: 160 }}>
                            <Tooltip title={key} arrow><span className='font-semibold truncate'>{key}</span></Tooltip>
                        </div>
                    ),
                    sortable: true,
                    minWidth: 180,
                    headerAlign: 'left',
                    align: 'left',
                    getSortValue: (row) => {
                        const { ape, accurate } = getFeatureParts(row, key);

                        if (accurate === true) { return '1-' + formatApe(ape); }
                        if (accurate === false) { return '0-' + formatApe(ape); }
                        return '-1-' + formatApe(ape);
                    },
                    exportValue: (row) => formatFeatureExportValue(row, key),
                    renderCell: ({ row }) => renderFeatureCell(row, key)
                };
            });

        return {
            columns: [...baseColumns, ...dynamicColumns],
            tableMinWidth: 140 + 120 + (visibleFeatureKeys.length * 180),
            sort: INITIAL_SORT,
            states: {
                loadingTitle: 'Loading model validation history...',
                errorTitle: 'Failed to load model validation history',
                emptyTitle: 'No model validation history available',
                noResultsTitle: 'No matching model validation rows'
            }
        };
    }, [featureKeys, visibleFeatureKeys]);

    const exportDisabled = modelValidationLogDataListLoading || !!modelValidationLogDataListError || !filteredRows.length;

    const handleExport = (formatType) => {
        exportPopupTable({
            tableConfig,
            rows: filteredRows,
            sort: tableSort,
            filename: buildExportFilename('model-validation-history', ts),
            format: formatType
        });
    };

    return (
        <OptimizationPipelinePopup open={!!popup} onClose={closePopup} width={popup?.width || '1440px'} minWidth={popup?.minWidth || 480} maxWidth={popup?.maxWidth || 1440} className={popup?.className}>

            <OptimizationPipelinePopupHeader title='Model Validation History' subtitle={getDateRangeSubtitle(dateRange)} icon={PiClockCounterClockwiseDuotone} onClose={closePopup}>
                <div className='flex items-center gap-8'>
                    <PopupDateRangeQuickSelector value={dateRange} onChange={setDateRange} minDate={dateBounds.minDate} maxDate={dateBounds.maxDate} disabledQuickOptions={disabledQuickOptions} />
                    <PopupExportButtons disabled={exportDisabled} onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} />
                </div>
            </OptimizationPipelinePopupHeader>

            {rows.length > 0 && (
                <PopupToolbar>
                    <PopupToolbarGroup label='Accurate:'>
                        <PopupToolbarChip label='All' active={accurateFilter === 'all'} onClick={() => { setAccurateFilter('all'); }} />
                        <PopupToolbarChip label='Has data' active={accurateFilter === 'has'} onClick={() => { setAccurateFilter('has'); }} />
                        <PopupToolbarChip label='Yes' active={accurateFilter === 'yes'} onClick={() => { setAccurateFilter('yes'); }} />
                        <PopupToolbarChip label='No' active={accurateFilter === 'no'} onClick={() => { setAccurateFilter('no'); }} />
                    </PopupToolbarGroup>

                    <PopupToolbarScrollGroup label='Columns:'>
                        {featureKeys.map((key) => {
                            const active = visibleFeatureKeys.includes(key);
                            return (<PopupToolbarChip key={key} label={key} active={active} onClick={() => { setVisibleFeatureKeys((prev) => { if (active) { return prev.filter((value) => value !== key); } return [...prev, key]; }); }} />);
                        })}
                    </PopupToolbarScrollGroup>
                </PopupToolbar>
            )}

            <OptimizationPipelinePopupTable
                tableConfig={tableConfig}
                rows={filteredRows}
                loading={modelValidationLogDataListLoading}
                error={modelValidationLogDataListError}
                minHeight={360}
                sort={tableSort}
                onSortChange={setTableSort}
                rowKey={(row, index) => row?.ts || index}
            />

        </OptimizationPipelinePopup>
    );
}